// Оценка экрана в предпросмотре: есть ли на нём что оценивать и каков вердикт.
// Считается ЛОКАЛЬНО и никуда не уходит: боевую проверку делает бэк, здесь
// автор репетирует собственный экран.
import type { Block } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { matchPairs, selectedOptions, yesNoValue, type AnswerValue } from './answerState'
import { exhaustive } from './exhaustive'
import { answerableOptions } from './quizOptions'

/**
 * Плеер знает два исхода (`isCorrect: boolean`), и своего третьего мы не
 * выдумываем — `accepted` НЕ третья оценка, а признак «оценки нет»: опрос без
 * эталонного ответа судить нечем, а зелёная плашка на догадке врала бы автору.
 */
export type Verdict = 'correct' | 'incorrect' | 'accepted'

const verdictOf = (ok: boolean): Verdict => (ok ? 'correct' : 'incorrect')

/**
 * Есть ли на экране, на что отвечать. Свежесозданный квиз приезжает с пустым
 * списком вариантов (`blockDefaults`), и без этой проверки он оценивался бы
 * вечно неотвеченным: кнопка навсегда погашена, репетиция упирается намертво.
 */
export const hasAnswerableInput = (block: Block, lang: ContentLang): boolean => {
  switch (block.type) {
    case 'quiz':
      // Да/нет спрашивает всегда: плитки «Да»/«Нет» не из данных блока.
      return block.quizType === 'yes_no' ? true : answerableOptions(block, lang).length > 0
    case 'match':
      return block.pairs.length > 0
    case 'info':
    case 'video':
    case 'article':
    case 'design':
      return false
    default:
      return exhaustive(block, false)
  }
}

const sameSet = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  const chosen = new Set(a)
  return b.every((index) => chosen.has(index))
}

/** `null` — экран не оценивается: контентный или без того, на что отвечать.
 *  Новый тип блока обязан сломать сборку здесь, а не молча попасть в `default`. */
export const checkAnswer = (
  block: Block,
  answer: AnswerValue,
  lang: ContentLang,
): Verdict | null => {
  if (!hasAnswerableInput(block, lang)) return null
  switch (block.type) {
    case 'quiz': {
      if (block.quizType === 'yes_no') {
        const value = yesNoValue(answer)
        return verdictOf(value !== null && value === block.correctAnswer)
      }
      // Правильные — среди НАЖИМАЕМЫХ: вариант без текста экран не предлагает,
      // и считать его частью ответа значило бы требовать ненажимаемое.
      const correct = answerableOptions(block, lang).flatMap((o) => (o.isCorrect ? [o.index] : []))
      return verdictOf(sameSet(selectedOptions(answer), correct))
    }
    case 'match': {
      // Верно — каждый термин в паре со своим описанием (индексы пар совпадают).
      const pairs = matchPairs(answer)
      return verdictOf(
        Object.keys(pairs).length === block.pairs.length &&
          block.pairs.every((_, left) => pairs[left] === left),
      )
    }
    case 'info':
    case 'video':
    case 'article':
    case 'design':
      return null
    default:
      return exhaustive(block, null)
  }
}
